/**
 * MarketplacePage Component
 *
 * Main page for discovering and installing marketplace applications.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { ListingCard } from '../components/ListingCard';
import { InstallAppModal } from '../components/InstallAppModal';
import { useMarketplaceListings } from '../hooks/useMarketplaceListings';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { MarketplaceListing, MarketplaceCategory } from '../../../types/marketplace.types';
import { MARKETPLACE_CATEGORIES } from '../../../types/marketplace.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * MarketplacePage component
 */
export const MarketplacePage: React.FC = () => {
  const { user, tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<MarketplaceCategory | ''>('');
  const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);
  const [showInstallModal, setShowInstallModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch marketplace listings
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketplaceListings({
    search: debouncedSearch || undefined,
    category: selectedCategory || undefined,
    limit: 20,
  });

  // Flatten paginated results
  const listings = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data]);

  const handleView = (listing: MarketplaceListing) => {
    // Navigate to listing detail page
    window.location.href = `/app/marketplace/${listing.listingId}`;
  };

  const handleInstall = (listing: MarketplaceListing) => {
    setSelectedListing(listing);
    setShowInstallModal(true);
  };

  const handleCloseInstallModal = () => {
    setShowInstallModal(false);
    setSelectedListing(null);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Application Marketplace</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Discover and install applications from the marketplace
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="card rounded-lg p-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
              <Input
                type="text"
                placeholder="Search applications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as MarketplaceCategory | '')}
                className="w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <option value="">All Categories</option>
                {MARKETPLACE_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} />
            <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Loading marketplace...</p>
          </div>
        )}

        {/* Listings Grid */}
        {!isLoading && listings.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <ListingCard
                  key={listing.listingId}
                  listing={listing}
                  onView={handleView}
                  onInstall={handleInstall}
                />
              ))}
            </div>

            {/* Load More */}
            {hasNextPage && (
              <div className="text-center">
                <Button
                  variant="secondary"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && listings.length === 0 && (
          <div className="text-center py-12 card rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
            <FunnelIcon className="h-12 w-12 mx-auto" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>No applications found</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {searchQuery || selectedCategory
                ? 'Try adjusting your search or filters'
                : 'Check back later for new applications'}
            </p>
          </div>
        )}

        {/* Install Modal */}
        {showInstallModal && selectedListing && (
          <InstallAppModal
            isOpen={showInstallModal}
            onClose={handleCloseInstallModal}
            listing={selectedListing}
            tenantId={tenantId}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default MarketplacePage;
